import { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Heart, Plus, Edit, Trash2, MapPin, Building2, Upload } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useMapWithProvider } from '@/hooks/useMapWithProvider';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useTenant } from '@/contexts/TenantContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface WishOrganisation {
  id: string;
  name: string;
  accent_color: string;
  logo_url: string | null;
  website: string | null;
  contact_person: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  tenant_id: string;
}

interface WishSite {
  id: string;
  name: string;
  organisation_id: string;
  address: string | null;
  city: string;
  latitude: number | null;
  longitude: number | null;
  priority: string;
  desired_chargers: number | null;
  desired_power_kw: number | null;
  timeline: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// Helper om wish favicon op te halen
const getWishLogo = (org: WishOrganisation): string => {
  if (org.logo_url) {
    return org.logo_url;
  }
  
  if (org.website) {
    const cleanDomain = org.website
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0];
    
    return `https://www.google.com/s2/favicons?sz=64&domain=${cleanDomain}`;
  }
  
  return '/favicon.png';
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'high': return 'bg-red-500';
    case 'medium': return 'bg-yellow-500';
    case 'low': return 'bg-green-500';
    default: return 'bg-gray-500';
  }
};

const getPriorityLabel = (priority: string) => {
  switch (priority) {
    case 'high': return 'Hoog';
    case 'medium': return 'Gemiddeld';
    case 'low': return 'Laag';
    default: return 'Onbekend';
  }
};

export default function ChargingWensen() {
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const { MapComponent } = useMapWithProvider();
  
  const inCRM = location.pathname.startsWith('/crm');
  const portalType = inCRM ? 'crm' : 'charging';
  
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
  const [isSiteModalOpen, setIsSiteModalOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<WishOrganisation | null>(null);
  const [selectedSite, setSelectedSite] = useState<WishSite | null>(null);
  const [siteOrgId, setSiteOrgId] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [orgFormData, setOrgFormData] = useState({
    name: '',
    accent_color: '#22c55e',
    website: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    notes: ''
  });
  const [siteFormData, setSiteFormData] = useState({
    name: '',
    address: '',
    city: '',
    notes: '',
    priority: 'medium',
    desired_chargers: '',
    desired_power_kw: '',
    timeline: ''
  });

  // Fetch organisations
  const {
    data: organisations = [],
    isLoading: isLoadingOrgs
  } = useQuery({
    queryKey: ['wish-organisations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wish_organisations')
        .select('*')
        .order('name');
      if (error) throw error;
      return data as WishOrganisation[];
    }
  });

  // Fetch sites
  const {
    data: sites = [],
    isLoading: isLoadingSites
  } = useQuery({
    queryKey: ['wish-sites'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wish_sites')
        .select('*')
        .order('name');
      if (error) throw error;
      return data as WishSite[];
    }
  });

  // Save organisation mutation
  const saveOrgMutation = useMutation({
    mutationFn: async (orgData: typeof orgFormData) => {
      let logoUrl = selectedOrg?.logo_url || null;

      if (logoFile) {
        const fileExt = logoFile.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('competitor-logos')
          .upload(fileName, logoFile);
        if (uploadError) throw uploadError;
        const { data: { publicUrl } } = supabase.storage
          .from('competitor-logos')
          .getPublicUrl(fileName);
        logoUrl = publicUrl;
      }

      const fullData = {
        ...orgData,
        logo_url: logoUrl,
        tenant_id: currentTenant?.id,
        created_by: user?.id
      };

      if (selectedOrg) {
        const { error } = await supabase
          .from('wish_organisations')
          .update(fullData)
          .eq('id', selectedOrg.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('wish_organisations')
          .insert(fullData);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wish-organisations'] });
      toast({ title: selectedOrg ? 'Bedrijf bijgewerkt' : 'Bedrijf toegevoegd' });
      closeOrgModal();
    },
    onError: (error: any) => {
      toast({ title: 'Fout bij opslaan', description: error.message, variant: 'destructive' });
    }
  });

  // Save site mutation
  const saveSiteMutation = useMutation({
    mutationFn: async (siteData: any) => {
      let latitude = siteData.latitude;
      let longitude = siteData.longitude;

      if ((!latitude || !longitude) && siteData.city) {
        const query = [siteData.address, siteData.city].filter(Boolean).join(', ');
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&countrycodes=nl&limit=1`,
          { headers: { 'User-Agent': 'Greenvision Charging Portal' } }
        );
        const geoData = await response.json();
        if (geoData && geoData.length > 0) {
          latitude = parseFloat(geoData[0].lat);
          longitude = parseFloat(geoData[0].lon);
        }
      }

      const fullData = {
        ...siteData,
        latitude,
        longitude,
        organisation_id: siteOrgId,
        desired_chargers: siteData.desired_chargers ? parseInt(siteData.desired_chargers) : null,
        desired_power_kw: siteData.desired_power_kw ? parseInt(siteData.desired_power_kw) : null
      };

      if (selectedSite) {
        const { error } = await supabase
          .from('wish_sites')
          .update(fullData)
          .eq('id', selectedSite.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('wish_sites')
          .insert(fullData);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wish-sites'] });
      toast({ title: selectedSite ? 'Wens bijgewerkt' : 'Wens toegevoegd' });
      closeSiteModal();
    },
    onError: (error: any) => {
      console.error('Site save error:', error);
      let description = error.message;
      
      if (error.message?.includes('row-level security')) {
        description = 'Je hebt geen rechten om deze wens toe te voegen.';
      } else if (error.message?.includes('organisation_id')) {
        description = 'Selecteer eerst een bedrijf voordat je een wens toevoegt.';
      } else if (error.code === '23503') {
        description = 'Het geselecteerde bedrijf bestaat niet meer.';
      }
      
      toast({ title: 'Fout bij opslaan wens', description, variant: 'destructive' });
    }
  });

  // Delete organisation mutation
  const deleteOrgMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('wish_organisations').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wish-organisations'] });
      queryClient.invalidateQueries({ queryKey: ['wish-sites'] });
      toast({ title: 'Bedrijf verwijderd' });
    }
  });

  // Delete site mutation
  const deleteSiteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('wish_sites').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wish-sites'] });
      toast({ title: 'Wens verwijderd' });
    }
  });

  // Group sites by organisation
  const sitesByOrg = useMemo(() => {
    const grouped: Record<string, WishSite[]> = {};
    sites.forEach(site => {
      if (!grouped[site.organisation_id]) {
        grouped[site.organisation_id] = [];
      }
      grouped[site.organisation_id].push(site);
    });
    return grouped;
  }, [sites]);

  // Map sites with filtering
  const filteredMapSites = useMemo(() => {
    const sitesWithCoords = sites.filter(s => s.latitude && s.longitude);
    const filtered = selectedOrgId 
      ? sitesWithCoords.filter(s => s.organisation_id === selectedOrgId) 
      : sitesWithCoords;
    
    return filtered.map(site => {
      const org = organisations.find(o => o.id === site.organisation_id);
      return {
        id: site.id,
        name: site.name,
        address: site.address || site.city,
        latitude: site.latitude!,
        longitude: site.longitude!,
        type: 'wish',
        color: org?.accent_color || '#22c55e',
        organisation_id: site.organisation_id
      };
    });
  }, [sites, organisations, selectedOrgId]);

  const getMarkerColor = (site: any) => {
    return site.color || '#22c55e';
  };

  const closeOrgModal = () => {
    setIsOrgModalOpen(false);
    setSelectedOrg(null);
    setOrgFormData({
      name: '',
      accent_color: '#22c55e',
      website: '',
      contact_person: '',
      contact_email: '',
      contact_phone: '',
      notes: ''
    });
    setLogoFile(null);
    setLogoPreview(null);
  };

  const closeSiteModal = () => {
    setIsSiteModalOpen(false);
    setSelectedSite(null);
    setSiteOrgId(null);
    setSiteFormData({
      name: '',
      address: '',
      city: '',
      notes: '',
      priority: 'medium',
      desired_chargers: '',
      desired_power_kw: '',
      timeline: ''
    });
  };

  const handleEditOrg = (org: WishOrganisation) => {
    setSelectedOrg(org);
    setOrgFormData({
      name: org.name,
      accent_color: org.accent_color,
      website: org.website || '',
      contact_person: org.contact_person || '',
      contact_email: org.contact_email || '',
      contact_phone: org.contact_phone || '',
      notes: org.notes || ''
    });
    setLogoPreview(org.logo_url);
    setIsOrgModalOpen(true);
  };

  const handleEditSite = (site: WishSite) => {
    setSelectedSite(site);
    setSiteOrgId(site.organisation_id);
    setSiteFormData({
      name: site.name,
      address: site.address || '',
      city: site.city,
      notes: site.notes || '',
      priority: site.priority || 'medium',
      desired_chargers: site.desired_chargers?.toString() || '',
      desired_power_kw: site.desired_power_kw?.toString() || '',
      timeline: site.timeline || ''
    });
    setIsSiteModalOpen(true);
  };

  const handleAddSite = (orgId: string) => {
    setSiteOrgId(orgId);
    setIsSiteModalOpen(true);
  };

  const handleSaveOrg = () => {
    if (!orgFormData.name) {
      toast({ title: 'Naam is verplicht', variant: 'destructive' });
      return;
    }
    saveOrgMutation.mutate(orgFormData);
  };

  const handleSaveSite = () => {
    if (!siteFormData.name || !siteFormData.city) {
      toast({ title: 'Naam en plaats zijn verplicht', variant: 'destructive' });
      return;
    }
    
    if (!siteOrgId) {
      toast({ 
        title: 'Selecteer een bedrijf', 
        description: 'Elke wens moet aan een bedrijf gekoppeld zijn', 
        variant: 'destructive' 
      });
      return;
    }
    
    saveSiteMutation.mutate(siteFormData);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="container mx-auto p-3 sm:p-6">
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Heart className="w-8 h-8 text-green-500" />
              Wensen
            </h1>
            <p className="text-muted-foreground mt-1">
              Beheer gewenste laadlocaties van transportbedrijven
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => {
                setSiteOrgId(null);
                setIsSiteModalOpen(true);
              }} 
              variant="outline"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Snel wens toevoegen</span>
            </Button>
            <Button onClick={() => setIsOrgModalOpen(true)}>
              <Building2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Nieuw bedrijf</span>
            </Button>
          </div>
        </div>

        {/* Map */}
        <Card className="p-4">
          <div className="w-full h-[500px] rounded-lg overflow-hidden">
            {filteredMapSites.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <MapPin className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Geen wensen op kaart</p>
                </div>
              </div>
            ) : (
              <MapComponent 
                sites={filteredMapSites} 
                getMarkerColor={getMarkerColor} 
                portalType={portalType} 
              />
            )}
          </div>
        </Card>

        {/* Logo Filter Bar */}
        <Card className="p-4">
          <div className="flex gap-3 items-center overflow-x-auto">
            <Button
              variant={selectedOrgId === null ? "default" : "outline"}
              onClick={() => setSelectedOrgId(null)}
              className="flex-shrink-0"
              size="sm"
            >
              Alles ({sites.length})
            </Button>
            {organisations.map(org => (
              <Button
                key={org.id}
                variant={selectedOrgId === org.id ? "default" : "outline"}
                onClick={() => setSelectedOrgId(org.id)}
                className="flex flex-col gap-1 h-auto p-3 flex-shrink-0"
                size="sm"
              >
                <img 
                  src={getWishLogo(org)} 
                  className="w-12 h-12 rounded object-contain bg-white p-1" 
                  alt={org.name}
                  onError={(e) => {
                    e.currentTarget.src = '/favicon.png';
                  }}
                />
                <span className="text-xs font-medium">{org.name}</span>
                <Badge variant="secondary" className="text-xs">
                  {sitesByOrg[org.id]?.length || 0}
                </Badge>
              </Button>
            ))}
          </div>
        </Card>

        {/* Organisations & Sites List */}
        <Card className="p-6">
          {isLoadingOrgs || isLoadingSites ? (
            <div className="text-center py-8">Laden...</div>
          ) : organisations.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Geen bedrijven toegevoegd</p>
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-2">
              {organisations.map(org => (
                <AccordionItem key={org.id} value={org.id} className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3 flex-1">
                      <img 
                        src={getWishLogo(org)} 
                        className="w-10 h-10 rounded object-contain bg-white p-1" 
                        alt={org.name}
                        onError={(e) => {
                          e.currentTarget.src = '/favicon.png';
                        }}
                      />
                      <div className="flex-1 text-left">
                        <h3 className="font-semibold">{org.name}</h3>
                        <p className="text-xs text-muted-foreground">
                          {sitesByOrg[org.id]?.length || 0} wensen
                        </p>
                        {org.contact_person && (
                          <p className="text-xs text-muted-foreground">
                            Contact: {org.contact_person}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditOrg(org);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddSite(org.id);
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm('Weet je zeker dat je dit bedrijf wilt verwijderen? Alle wensen worden ook verwijderd.')) {
                              deleteOrgMutation.mutate(org.id);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pt-4">
                      {sitesByOrg[org.id]?.length > 0 ? (
                        sitesByOrg[org.id].map(site => (
                          <Card key={site.id} className="p-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-medium">{site.name}</h4>
                                  <Badge 
                                    variant="outline" 
                                    className={cn("text-xs", getPriorityColor(site.priority))}
                                  >
                                    {getPriorityLabel(site.priority)}
                                  </Badge>
                                </div>
                                {site.address && (
                                  <p className="text-sm text-muted-foreground">{site.address}</p>
                                )}
                                <p className="text-sm text-muted-foreground">{site.city}</p>
                                <div className="flex gap-2 mt-1 flex-wrap">
                                  {site.desired_chargers && (
                                    <Badge variant="outline" className="text-xs">
                                      {site.desired_chargers} laders gewenst
                                    </Badge>
                                  )}
                                  {site.desired_power_kw && (
                                    <Badge variant="outline" className="text-xs">
                                      {site.desired_power_kw} kW gewenst
                                    </Badge>
                                  )}
                                  {site.timeline && (
                                    <Badge variant="outline" className="text-xs">
                                      {site.timeline}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleEditSite(site)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => {
                                    if (confirm('Weet je zeker dat je deze wens wilt verwijderen?')) {
                                      deleteSiteMutation.mutate(site.id);
                                    }
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </Card>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Geen wensen toegevoegd
                        </p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </Card>
      </div>

      {/* Organisation Modal */}
      <Dialog open={isOrgModalOpen} onOpenChange={setIsOrgModalOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedOrg ? 'Bewerk bedrijf' : 'Nieuw bedrijf'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Naam *</label>
              <Input
                value={orgFormData.name}
                onChange={(e) => setOrgFormData({ ...orgFormData, name: e.target.value })}
                placeholder="Bedrijfsnaam..."
              />
            </div>
            <div>
              <label className="text-sm font-medium">Accentkleur *</label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={orgFormData.accent_color}
                  onChange={(e) => setOrgFormData({ ...orgFormData, accent_color: e.target.value })}
                  className="w-20 h-10"
                />
                <Input
                  value={orgFormData.accent_color}
                  onChange={(e) => setOrgFormData({ ...orgFormData, accent_color: e.target.value })}
                  placeholder="#22c55e"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Website</label>
              <Input
                value={orgFormData.website}
                onChange={(e) => setOrgFormData({ ...orgFormData, website: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="text-sm font-medium">Contactpersoon</label>
              <Input
                value={orgFormData.contact_person}
                onChange={(e) => setOrgFormData({ ...orgFormData, contact_person: e.target.value })}
                placeholder="Naam contactpersoon..."
              />
            </div>
            <div>
              <label className="text-sm font-medium">E-mailadres</label>
              <Input
                type="email"
                value={orgFormData.contact_email}
                onChange={(e) => setOrgFormData({ ...orgFormData, contact_email: e.target.value })}
                placeholder="email@bedrijf.nl"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Telefoonnummer</label>
              <Input
                value={orgFormData.contact_phone}
                onChange={(e) => setOrgFormData({ ...orgFormData, contact_phone: e.target.value })}
                placeholder="+31 6 12345678"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Notities</label>
              <Textarea
                value={orgFormData.notes}
                onChange={(e) => setOrgFormData({ ...orgFormData, notes: e.target.value })}
                placeholder="Extra informatie..."
                rows={3}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Logo</label>
              <div className="space-y-2">
                {logoPreview && (
                  <div className="w-24 h-24 border rounded-lg overflow-hidden bg-white flex items-center justify-center">
                    <img src={logoPreview} alt="Logo preview" className="max-w-full max-h-full object-contain" />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('logo-upload')?.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {logoPreview ? 'Wijzig logo' : 'Upload logo'}
                  </Button>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoChange}
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={closeOrgModal}>
                Annuleren
              </Button>
              <Button onClick={handleSaveOrg} disabled={saveOrgMutation.isPending}>
                {saveOrgMutation.isPending ? 'Opslaan...' : 'Opslaan'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Site Modal */}
      <Dialog open={isSiteModalOpen} onOpenChange={setIsSiteModalOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedSite ? 'Bewerk wens' : 'Nieuwe wens'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Bedrijf *</label>
              <Select value={siteOrgId || ''} onValueChange={(value) => setSiteOrgId(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer bedrijf..." />
                </SelectTrigger>
                <SelectContent>
                  {organisations.map(org => (
                    <SelectItem key={org.id} value={org.id}>
                      <div className="flex items-center gap-2">
                        <img 
                          src={getWishLogo(org)} 
                          className="w-5 h-5 rounded object-contain bg-white p-0.5" 
                          alt={org.name}
                          onError={(e) => {
                            e.currentTarget.src = '/favicon.png';
                          }}
                        />
                        {org.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Naam *</label>
              <Input
                value={siteFormData.name}
                onChange={(e) => setSiteFormData({ ...siteFormData, name: e.target.value })}
                placeholder="Locatienaam..."
              />
            </div>
            <div>
              <label className="text-sm font-medium">Adres</label>
              <Input
                value={siteFormData.address}
                onChange={(e) => setSiteFormData({ ...siteFormData, address: e.target.value })}
                placeholder="Straat + nummer..."
              />
            </div>
            <div>
              <label className="text-sm font-medium">Plaats *</label>
              <Input
                value={siteFormData.city}
                onChange={(e) => setSiteFormData({ ...siteFormData, city: e.target.value })}
                placeholder="Plaatsnaam..."
              />
            </div>
            <div>
              <label className="text-sm font-medium">Prioriteit</label>
              <Select 
                value={siteFormData.priority} 
                onValueChange={(value) => setSiteFormData({ ...siteFormData, priority: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">Hoog</SelectItem>
                  <SelectItem value="medium">Gemiddeld</SelectItem>
                  <SelectItem value="low">Laag</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Gewenste laders</label>
                <Input
                  type="number"
                  value={siteFormData.desired_chargers}
                  onChange={(e) => setSiteFormData({ ...siteFormData, desired_chargers: e.target.value })}
                  placeholder="12"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Gewenst vermogen (kW)</label>
                <Input
                  type="number"
                  value={siteFormData.desired_power_kw}
                  onChange={(e) => setSiteFormData({ ...siteFormData, desired_power_kw: e.target.value })}
                  placeholder="350"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Timeline</label>
              <Input
                value={siteFormData.timeline}
                onChange={(e) => setSiteFormData({ ...siteFormData, timeline: e.target.value })}
                placeholder="Bijv. Q2 2025"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Notities</label>
              <Textarea
                value={siteFormData.notes}
                onChange={(e) => setSiteFormData({ ...siteFormData, notes: e.target.value })}
                placeholder="Extra informatie..."
                rows={3}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={closeSiteModal}>
                Annuleren
              </Button>
              <Button onClick={handleSaveSite} disabled={saveSiteMutation.isPending}>
                {saveSiteMutation.isPending ? 'Opslaan...' : 'Opslaan'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
